import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Mail, Phone, MapPin, Calendar, Plus, Users, Building2, Wrench } from 'lucide-react';

const CRMContactDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [contact, setContact] = useState(null);
  const [activities, setActivities] = useState([]);
  const [companyContacts, setCompanyContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityForm, setActivityForm] = useState({
    activity_type: 'note',
    subject: '',
    description: '',
    duration_minutes: ''
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchContactDetails();
  }, [id]);

  const fetchContactDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch contact
      const contactRes = await fetch(`${backendUrl}/api/crm/contacts/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (contactRes.ok) {
        const contactData = await contactRes.json();
        setContact(contactData);
        
        // Fetch company contacts if contact has a company
        if (contactData.company) {
          const companyContactsRes = await fetch(`${backendUrl}/api/crm/contacts/${id}/company-contacts`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (companyContactsRes.ok) {
            const companyContactsData = await companyContactsRes.json();
            setCompanyContacts(companyContactsData);
          }
        }
      }

      // Fetch activities
      const activitiesRes = await fetch(`${backendUrl}/api/crm/contacts/${id}/activities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (activitiesRes.ok) {
        const activitiesData = await activitiesRes.json();
        setActivities(activitiesData);
      }
    } catch (error) {
      console.error('Error fetching contact details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/crm/activities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contact_id: id,
          ...activityForm,
          duration_minutes: activityForm.duration_minutes ? parseInt(activityForm.duration_minutes) : null
        })
      });

      if (response.ok) {
        setShowActivityModal(false);
        setActivityForm({
          activity_type: 'note',
          subject: '',
          description: '',
          duration_minutes: ''
        });
        fetchContactDetails();
      } else {
        alert('Failed to add activity');
      }
    } catch (error) {
      console.error('Error adding activity:', error);
      alert('Error adding activity');
    }
  };

  const getContactIcon = (type) => {
    switch (type) {
      case 'customer': return <Users className="text-blue-600" size={24} />;
      case 'vendor': return <Building2 className="text-purple-600" size={24} />;
      case 'subcontractor': return <Wrench className="text-orange-600" size={24} />;
      default: return <Users className="text-gray-600" size={24} />;
    }
  };

  const getActivityIcon = (type) => {
    const icons = {
      note: '📝',
      call: '📞',
      email: '📧',
      meeting: '🤝'
    };
    return icons[type] || '📝';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Toronto'
    });
  };

  const formatContactType = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Contact Not Found</h2>
          <button
            onClick={() => navigate('/crm/contacts')}
            className="text-blue-600 hover:text-blue-800"
          >
            Back to Contacts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/crm/contacts')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Contacts
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contact Info Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-2xl font-bold">
                    {contact.first_name[0]}{contact.last_name[0]}
                  </div>
                  {getContactIcon(contact.contact_type)}
                </div>
                <button
                  onClick={() => navigate(`/crm/contacts/${id}/edit`)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Edit size={20} className="text-gray-600" />
                </button>
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                {contact.first_name} {contact.last_name}
              </h1>
              <p className="text-sm text-gray-600 mb-4">{formatContactType(contact.contact_type)}</p>

              {contact.company && (
                <div className="mb-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/crm/company/${encodeURIComponent(contact.company)}`);
                    }}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-left"
                  >
                    {contact.company}
                  </button>
                  {companyContacts.length > 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      {companyContacts.length + 1} {companyContacts.length === 0 ? 'contact' : 'contacts'} at this company
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-3 mb-6">
                {contact.email && (
                  <div className="flex items-center gap-3 text-sm">
                    <Mail size={16} className="text-slate-500" />
                    <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">
                      {contact.email}
                    </a>
                  </div>
                )}
                {contact.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone size={16} className="text-slate-500" />
                    <a href={`tel:${contact.phone}`} className="text-gray-700">
                      {contact.phone}
                    </a>
                  </div>
                )}
                {contact.mobile && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone size={16} className="text-slate-500" />
                    <a href={`tel:${contact.mobile}`} className="text-gray-700">
                      {contact.mobile} (Mobile)
                    </a>
                  </div>
                )}
                {(contact.address || contact.city) && (
                  <div className="flex items-start gap-3 text-sm">
                    <MapPin size={16} className="text-slate-500 mt-0.5" />
                    <div className="text-gray-700">
                      {contact.address && <div>{contact.address}</div>}
                      {contact.city && (
                        <div>
                          {contact.city}{contact.province && `, ${contact.province}`} {contact.postal_code}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {contact.last_contact_date && (
                <div className="border-t pt-4 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar size={16} />
                    <span>Last contact: {formatDate(contact.last_contact_date)}</span>
                  </div>
                </div>
              )}

              {contact.tags && contact.tags.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {contact.tags.map(tag => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {contact.notes && (
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Notes</p>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{contact.notes}</p>
                </div>
              )}
            </div>

            {/* Company Contacts Section */}
            {contact.company && companyContacts.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Other Contacts at {contact.company}
                </h3>
                <div className="space-y-3">
                  {companyContacts.map((coworker) => (
                    <div
                      key={coworker.id}
                      onClick={() => navigate(`/crm/contacts/${coworker.id}`)}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                    >
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                        {coworker.first_name[0]}{coworker.last_name[0]}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {coworker.first_name} {coworker.last_name}
                        </p>
                        {coworker.email && (
                          <p className="text-sm text-gray-600">{coworker.email}</p>
                        )}
                      </div>
                      {coworker.phone && (
                        <p className="text-sm text-gray-600">{coworker.phone}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Activity Timeline */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Activity Timeline</h2>
                <button
                  onClick={() => setShowActivityModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Plus size={20} />
                  Log Activity
                </button>
              </div>

              {activities.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-3">📝</div>
                  <p className="text-gray-600 mb-4">No activities yet</p>
                  <button
                    onClick={() => setShowActivityModal(true)}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Log your first activity
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {activities.map((activity, index) => (
                    <div key={activity.id} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="text-3xl">{getActivityIcon(activity.activity_type)}</div>
                        {index < activities.length - 1 && (
                          <div className="w-0.5 h-full bg-gray-200 mt-2"></div>
                        )}
                      </div>
                      <div className="flex-1 pb-6">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="font-semibold text-gray-900">{activity.subject}</h3>
                            <span className="text-xs text-gray-500 capitalize px-2 py-1 bg-white rounded">
                              {activity.activity_type}
                            </span>
                          </div>
                          {activity.description && (
                            <p className="text-gray-700 text-sm mb-3 whitespace-pre-wrap">{activity.description}</p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>{activity.created_by_name || activity.created_by}</span>
                            <span>•</span>
                            <span>{formatDate(activity.created_at)}</span>
                            {activity.duration_minutes && (
                              <>
                                <span>•</span>
                                <span>{activity.duration_minutes} min</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Activity Modal */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Log Activity</h3>
            <form onSubmit={handleAddActivity}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Activity Type</label>
                <select
                  value={activityForm.activity_type}
                  onChange={(e) => setActivityForm({ ...activityForm, activity_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="note">Note</option>
                  <option value="call">Call</option>
                  <option value="email">Email</option>
                  <option value="meeting">Meeting</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                <input
                  type="text"
                  required
                  value={activityForm.subject}
                  onChange={(e) => setActivityForm({ ...activityForm, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={activityForm.description}
                  onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes)</label>
                <input
                  type="number"
                  min="0"
                  value={activityForm.duration_minutes}
                  onChange={(e) => setActivityForm({ ...activityForm, duration_minutes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowActivityModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Log Activity
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMContactDetail;